import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Rectangle, Circle, useMapEvents } from 'react-leaflet';
import { Plus, Play, Trash2, DollarSign, Map } from 'lucide-react';
import toast from 'react-hot-toast';

import { MetroMap, MetroBounds } from '@/components/map';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageSpinner } from '@/components/ui/Spinner';
import { metrosApi, type Metro, type Region } from '@/api/endpoints/metros';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

type Mode = 'view' | 'create' | 'detail';

interface DrawingState {
  start: { lat: number; lng: number } | null;
  end: { lat: number; lng: number } | null;
}

// Component to handle map click events for drawing
function DrawingHandler({
  onFirstClick,
  onSecondClick,
  hasFirstClick
}: {
  onFirstClick: (lat: number, lng: number) => void;
  onSecondClick: (lat: number, lng: number) => void;
  hasFirstClick: boolean;
}) {
  useMapEvents({
    click: (e) => {
      console.log('Draw click:', { hasFirstClick, lat: e.latlng.lat, lng: e.latlng.lng });
      if (!hasFirstClick) {
        onFirstClick(e.latlng.lat, e.latlng.lng);
      } else {
        onSecondClick(e.latlng.lat, e.latlng.lng);
      }
    }
  });

  return null;
}

export function MetrosPage() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('view');
  const [selectedMetro, setSelectedMetro] = useState<Metro | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
  const [drawing, setDrawing] = useState<DrawingState>({ start: null, end: null });
  const [newMetro, setNewMetro] = useState({
    name: '',
    slug: '',
    timeZone: 'America/Chicago'
  });

  // Fetch metros
  const { data: metrosData, isLoading: metrosLoading } = useQuery({
    queryKey: ['metros'],
    queryFn: metrosApi.list
  });

  // Fetch cost estimate
  const { data: estimateData } = useQuery({
    queryKey: ['discovery-estimate'],
    queryFn: metrosApi.getEstimate
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: metrosApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      queryClient.invalidateQueries({ queryKey: ['discovery-estimate'] });
      toast.success(`Created ${data.metro.name}. Run discovery to find bars.`);
      resetCreateMode();
    },
    onError: () => {
      toast.error('Failed to create metro');
    }
  });

  // Discover mutation
  const discoverMutation = useMutation({
    mutationFn: (id: string) => metrosApi.discover(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      queryClient.invalidateQueries({ queryKey: ['metro', selectedMetro?.id] });
      const results = data.results;
      toast.success(`Discovery completed: ${results.totalRegions} regions, ${results.totalBars.created} bars found`);
    },
    onError: () => {
      toast.error('Failed to run discovery');
    }
  });

  // Disable mutation
  const disableMutation = useMutation({
    mutationFn: (id: string) => metrosApi.disable(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metros'] });
      queryClient.invalidateQueries({ queryKey: ['discovery-estimate'] });
      setSelectedMetro(null);
      setMode('view');
      toast.success('Metro disabled');
    },
    onError: () => {
      toast.error('Failed to disable metro');
    }
  });

  const metros = metrosData?.metros || [];
  const estimate = estimateData?.estimate;

  // Filter metros with valid coordinates
  const validMetros = useMemo(() =>
    metros.filter(m =>
      m.neLatitude != null &&
      m.neLongitude != null &&
      m.swLatitude != null &&
      m.swLongitude != null
    ),
    [metros]
  );

  // Calculate map bounds from all valid metros
  const mapBounds = useMemo(() => {
    if (validMetros.length === 0) return undefined;

    const lats = validMetros.flatMap(m => [Number(m.neLatitude), Number(m.swLatitude)]);
    const lngs = validMetros.flatMap(m => [Number(m.neLongitude), Number(m.swLongitude)]);

    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    ] as [[number, number], [number, number]];
  }, [validMetros]);

  // Get bounds for selected metro
  const selectedBounds = selectedMetro &&
    selectedMetro.swLatitude != null &&
    selectedMetro.neLatitude != null
    ? [
        [Number(selectedMetro.swLatitude), Number(selectedMetro.swLongitude)],
        [Number(selectedMetro.neLatitude), Number(selectedMetro.neLongitude)]
      ] as [[number, number], [number, number]]
    : undefined;

  // Drawing bounds for preview
  const drawingBounds = drawing.start && drawing.end
    ? [
        [Math.min(drawing.start.lat, drawing.end.lat), Math.min(drawing.start.lng, drawing.end.lng)],
        [Math.max(drawing.start.lat, drawing.end.lat), Math.max(drawing.start.lng, drawing.end.lng)]
      ] as [[number, number], [number, number]]
    : undefined;

  function resetCreateMode() {
    setMode('view');
    setDrawing({ start: null, end: null });
    setNewMetro({ name: '', slug: '', timeZone: 'America/Chicago' });
  }

  function handleDrawStart(lat: number, lng: number) {
    setDrawing({ start: { lat, lng }, end: null });
  }

  function handleDrawEnd(lat: number, lng: number) {
    setDrawing(prev => ({ ...prev, end: { lat, lng } }));
  }

  function handleCreate() {
    if (!drawing.start || !drawing.end || !newMetro.name || !newMetro.slug) {
      toast.error('Please draw a rectangle and fill in name/slug');
      return;
    }

    createMutation.mutate({
      name: newMetro.name,
      slug: newMetro.slug,
      boundingBox: {
        ne: {
          lat: Math.max(drawing.start.lat, drawing.end.lat),
          lng: Math.max(drawing.start.lng, drawing.end.lng)
        },
        sw: {
          lat: Math.min(drawing.start.lat, drawing.end.lat),
          lng: Math.min(drawing.start.lng, drawing.end.lng)
        }
      },
      timeZone: newMetro.timeZone
    });
  }

  async function handleMetroClick(metro: Metro) {
    setSelectedMetro(metro);
    setMode('detail');

    // Fetch regions for this metro
    try {
      const data = await metrosApi.get(metro.id);
      setSelectedRegions(data.regions || []);
    } catch {
      setSelectedRegions([]);
    }
  }

  // Get depth-based color for regions
  function getRegionColor(depth: number): string {
    const colors = [
      '#22c55e', // depth 0 - green
      '#3b82f6', // depth 1 - blue
      '#8b5cf6', // depth 2 - purple
      '#f59e0b', // depth 3 - amber
      '#ef4444', // depth 4 - red
      '#ec4899', // depth 5 - pink
    ];
    return colors[Math.min(depth, colors.length - 1)];
  }

  if (metrosLoading) {
    return <PageSpinner />;
  }

  return (
    <div className="h-[calc(100vh-120px)] lg:h-[calc(100vh-72px)] flex -mx-4 lg:-mx-6 -mb-4 lg:-mb-6 -mt-0">
      {/* Sidebar */}
      <div className="w-80 border-r border-[var(--color-border)] flex flex-col overflow-hidden">
        {/* Cost Estimation Panel */}
        {estimate && (
          <Card className="m-4 mb-2">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Discovery Cost Estimate
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2 text-sm">
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Active Regions:</span>
                  <span>{estimate.activeRegions.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Active Bars:</span>
                  <span>{estimate.activeBars.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Free Tier:</span>
                  <span>{estimate.freeAllowance.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Est. Cost:</span>
                  <span className={estimate.estimatedCost > 0 ? 'text-amber-500' : 'text-green-500'}>
                    ${estimate.estimatedCost.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metro List or Create Form */}
        <div className="flex-1 overflow-y-auto p-4 pt-2">
          {mode === 'create' ? (
            <div className="space-y-4">
              <h3 className="font-semibold">Create New Metro</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Click two points on the map to draw a bounding box.
              </p>

              <Input
                placeholder="Metro Name"
                value={newMetro.name}
                onChange={(e) => setNewMetro(prev => ({ ...prev, name: e.target.value }))}
              />
              <Input
                placeholder="Slug (e.g., kansas-city)"
                value={newMetro.slug}
                onChange={(e) => setNewMetro(prev => ({ ...prev, slug: e.target.value }))}
              />
              <Select
                value={newMetro.timeZone}
                onChange={(e) => setNewMetro(prev => ({ ...prev, timeZone: e.target.value }))}
              >
                {US_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </Select>

              {drawing.start && drawing.end && (
                <div className="text-sm text-[var(--color-muted)]">
                  Bounds: {drawing.start.lat.toFixed(4)}, {drawing.start.lng.toFixed(4)} to{' '}
                  {drawing.end.lat.toFixed(4)}, {drawing.end.lng.toFixed(4)}
                </div>
              )}

              <div className="p-3 bg-blue-500/10 rounded-lg text-sm">
                <div className="font-medium text-blue-400">Adaptive Discovery</div>
                <div className="text-[var(--color-muted)]">
                  Regions will be created automatically during discovery based on bar density.
                </div>
              </div>

              <Button
                variant="primary"
                className="w-full"
                onClick={handleCreate}
                disabled={!drawing.start || !drawing.end || !newMetro.name || !newMetro.slug || createMutation.isPending}
              >
                Create Metro
              </Button>
              <Button variant="ghost" className="w-full" onClick={resetCreateMode}>
                Cancel
              </Button>
            </div>
          ) : mode === 'detail' && selectedMetro ? (
            <div className="space-y-4">
              <h3 className="font-semibold">{selectedMetro.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Regions:</span>
                  <span>{selectedMetro.regionCount ?? 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Bars:</span>
                  <span>{selectedMetro.barCount ?? 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-muted)]">Status:</span>
                  <span className={selectedMetro.isActive ? 'text-green-500' : 'text-red-500'}>
                    {selectedMetro.isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>
                {selectedMetro.lastDiscoveredAt && (
                  <div className="flex justify-between">
                    <span className="text-[var(--color-muted)]">Last Discovery:</span>
                    <span>{new Date(selectedMetro.lastDiscoveredAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {selectedRegions.length > 0 && (
                <div className="p-3 bg-green-500/10 rounded-lg text-sm">
                  <div className="font-medium text-green-400 flex items-center gap-2">
                    <Map className="w-4 h-4" />
                    {selectedRegions.length} Regions
                  </div>
                  <div className="text-[var(--color-muted)] mt-1">
                    Showing on map. Colors indicate subdivision depth.
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => discoverMutation.mutate(selectedMetro.id)}
                  disabled={discoverMutation.isPending}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {discoverMutation.isPending ? 'Running...' : 'Run Discovery'}
                </Button>
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={() => {
                    if (confirm(`Disable ${selectedMetro.name}?`)) {
                      disableMutation.mutate(selectedMetro.id);
                    }
                  }}
                  disabled={disableMutation.isPending}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Disable Metro
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => { setMode('view'); setSelectedMetro(null); setSelectedRegions([]); }}>
                  Back to List
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Metros</h3>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setMode('create')}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>

              {metros.length === 0 ? (
                <p className="text-sm text-[var(--color-muted)]">No metros configured.</p>
              ) : (
                <div className="space-y-2">
                  {metros.map(metro => (
                    <button
                      key={metro.id}
                      className="w-full p-3 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-card)] transition-colors text-left"
                      onClick={() => handleMetroClick(metro)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{metro.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${metro.isActive ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                          {metro.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                      <div className="text-sm text-[var(--color-muted)] mt-1">
                        {metro.regionCount ?? 0} regions &middot; {metro.barCount ?? 0} bars
                      </div>
                      {metro.neLatitude == null && (
                        <div className="text-xs text-amber-500 mt-1">Missing coordinates</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1">
        <MetroMap
          bounds={mode === 'detail' && selectedBounds ? selectedBounds : mapBounds}
          className="h-full"
        >
          {/* Existing metros */}
          {validMetros.map(metro => (
            <MetroBounds
              key={metro.id}
              metro={metro}
              selected={selectedMetro?.id === metro.id}
              onClick={handleMetroClick}
            />
          ))}

          {/* Drawing handler - only rendered in create mode */}
          {mode === 'create' && (
            <DrawingHandler
              hasFirstClick={drawing.start !== null}
              onFirstClick={handleDrawStart}
              onSecondClick={handleDrawEnd}
            />
          )}

          {/* Drawing preview rectangle */}
          {mode === 'create' && drawingBounds && (
            <Rectangle
              bounds={drawingBounds}
              pathOptions={{
                color: '#3b82f6',
                fillColor: '#3b82f6',
                fillOpacity: 0.2,
                weight: 2,
                dashArray: '5, 5'
              }}
            />
          )}

          {/* Region circles for selected metro */}
          {mode === 'detail' && selectedRegions.map(region => (
            <Circle
              key={region.id}
              center={[region.latitude, region.longitude]}
              radius={region.radius}
              pathOptions={{
                color: getRegionColor(region.depth || 0),
                fillColor: getRegionColor(region.depth || 0),
                fillOpacity: 0.15,
                weight: 1
              }}
            />
          ))}

        </MetroMap>
      </div>
    </div>
  );
}
